import type { ReactNode, Ref } from 'react';
import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import classNames from 'classnames';

import styles from './RadioGroup.module.scss';

export interface RadioGroupProps extends Omit<RadixRadioGroup.RadioGroupProps, 'asChild'> {
    className?: string;
    direction?: 'vertical' | 'horizontal';
    ref?: Ref<HTMLDivElement>;
}

export interface RadioProps extends Omit<RadixRadioGroup.RadioGroupItemProps, 'asChild'> {
    /** Class for the round button. */
    className?: string;
    /** Class for the label wrapping button and text. */
    labelClassName?: string;
    label?: ReactNode;
    ref?: Ref<HTMLButtonElement>;
}

/**
 * A group of radios. Arrow keys move the selection inside the group and Tab leaves it, as
 * with native radios; with a `name` inside a `<form>` the chosen value is submitted.
 */
export function RadioGroup(props: RadioGroupProps) {
    const { ref, className, direction = 'vertical', ...radioGroupProps } = props;

    return (
        <RadixRadioGroup.Root
            ref={ref}
            {...radioGroupProps}
            className={classNames(
                styles.root,
                direction === 'horizontal' && styles.horizontal,
                className
            )}
        />
    );
}

/** One option of a RadioGroup; the label wraps the button, so clicking the text selects it. */
export function Radio(props: RadioProps) {
    const { ref, className, labelClassName, label, ...itemProps } = props;

    return (
        <label
            className={classNames(
                styles.option,
                itemProps.disabled && styles.optionDisabled,
                labelClassName
            )}>
            <RadixRadioGroup.Item
                ref={ref}
                {...itemProps}
                className={classNames(styles.item, className)}>
                <RadixRadioGroup.Indicator className={styles.indicator} />
            </RadixRadioGroup.Item>
            {label !== undefined && <span className={styles.label}>{label}</span>}
        </label>
    );
}
